import { Box, Button, Container, Typography, Card, CardContent, Stack, Chip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SpeedIcon from '@mui/icons-material/Speed';
import DescriptionIcon from '@mui/icons-material/Description';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';

export default function Landing() {
  const navigate = useNavigate();

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Navigation Bar */}
      <Box
        component="nav"
        sx={{
          py: 2,
          px: 3,
          borderBottom: '1px solid',
          borderColor: 'divider',
          position: 'sticky',
          top: 0,
          zIndex: 1000,
          backdropFilter: 'blur(10px)',
          bgcolor: 'rgba(255, 255, 255, 0.9)',
        }}
      >
        <Container maxWidth="lg">
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="h5" component="h1" sx={{ fontWeight: 700, color: 'primary.main' }}>
              Grant Proposal Writer
            </Typography>
            <Stack direction="row" spacing={2}>
              <Button variant="outlined" onClick={() => navigate('/login')}>
                Log In
              </Button>
              <Button variant="contained" onClick={() => navigate('/signup')}>
                Get Started Free
              </Button>
            </Stack>
          </Stack>
        </Container>
      </Box>

      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          py: { xs: 8, md: 12 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 6, alignItems: 'center' }}>
            <Box>
              <Chip
                label="AI-Powered Grant Writing"
                icon={<AutoAwesomeIcon />}
                sx={{
                  mb: 3,
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  fontWeight: 600,
                }}
              />
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: '2.5rem', md: '3.5rem' },
                  fontWeight: 800,
                  mb: 3,
                  lineHeight: 1.2,
                }}
              >
                Win More Grants in Less Time
              </Typography>
              <Typography variant="h5" sx={{ mb: 4, opacity: 0.95, fontWeight: 400, lineHeight: 1.6 }}>
                Transform RFPs into compelling, tailored grant proposals using AI.
                Generate professional proposals in hours, not weeks.
              </Typography>
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate('/signup')}
                sx={{
                  bgcolor: 'white',
                  color: 'primary.main',
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.9)',
                  },
                }}
                startIcon={<RocketLaunchIcon />}
              >
                Start Free Trial
              </Button>
            </Box>
            <Box>
              <Box
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: 3,
                  p: 3,
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                }}
              >
                <img
                  src="/images/hero-screenshot.png"
                  alt="Grant Proposal Writer Interface - Real-time question progress tracking"
                  style={{ width: '100%', borderRadius: '12px', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}
                />
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Social Proof Section */}
      <Box sx={{ py: 6, bgcolor: 'background.paper' }}>
        <Container maxWidth="lg">
          <Stack spacing={4} alignItems="center">
            <Typography variant="h6" color="text.secondary" textAlign="center">
              Trusted by nonprofits and grant-seeking organizations
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 4, justifyContent: 'center' }}>
              <Box>
                <Stack alignItems="center" spacing={1}>
                  <Typography variant="h3" color="primary.main" fontWeight={700}>
                    2 hrs
                  </Typography>
                  <Typography variant="body2" color="text.secondary" textAlign="center">
                    Average proposal completion time
                  </Typography>
                </Stack>
              </Box>
              <Box>
                <Stack alignItems="center" spacing={1}>
                  <Typography variant="h3" color="primary.main" fontWeight={700}>
                    10x
                  </Typography>
                  <Typography variant="body2" color="text.secondary" textAlign="center">
                    Faster than traditional methods
                  </Typography>
                </Stack>
              </Box>
              <Box>
                <Stack alignItems="center" spacing={1}>
                  <Typography variant="h3" color="primary.main" fontWeight={700}>
                    24/7
                  </Typography>
                  <Typography variant="body2" color="text.secondary" textAlign="center">
                    AI-powered assistance
                  </Typography>
                </Stack>
              </Box>
              <Box>
                <Stack alignItems="center" spacing={1}>
                  <Typography variant="h3" color="primary.main" fontWeight={700}>
                    100%
                  </Typography>
                  <Typography variant="body2" color="text.secondary" textAlign="center">
                    Customizable to your org
                  </Typography>
                </Stack>
              </Box>
            </Box>
          </Stack>
        </Container>
      </Box>

      {/* Features Section */}
      <Box sx={{ py: 10, bgcolor: 'background.default' }}>
        <Container maxWidth="lg">
          <Stack spacing={2} alignItems="center" mb={8}>
            <Typography variant="h2" textAlign="center" fontWeight={700}>
              Everything You Need to Win Grants
            </Typography>
            <Typography variant="h6" color="text.secondary" textAlign="center" maxWidth="800px">
              Our AI-powered platform streamlines every step of the grant proposal process
            </Typography>
          </Stack>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 4 }}>
            <Box>
              <Card elevation={2} sx={{ height: '100%', transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-4px)' } }}>
                <CardContent sx={{ p: 4 }}>
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: 2,
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 3,
                    }}
                  >
                    <DescriptionIcon sx={{ fontSize: 32, color: 'white' }} />
                  </Box>
                  <Typography variant="h5" fontWeight={600} gutterBottom>
                    Intelligent RFP Analysis
                  </Typography>
                  <Typography color="text.secondary" sx={{ lineHeight: 1.8 }}>
                    Upload any RFP document and our AI automatically extracts requirements, deadlines, and focus areas.
                    No more manual parsing through dense documents.
                  </Typography>
                </CardContent>
              </Card>
            </Box>

            <Box>
              <Card elevation={2} sx={{ height: '100%', transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-4px)' } }}>
                <CardContent sx={{ p: 4 }}>
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: 2,
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 3,
                    }}
                  >
                    <AutoAwesomeIcon sx={{ fontSize: 32, color: 'white' }} />
                  </Box>
                  <Typography variant="h5" fontWeight={600} gutterBottom>
                    Context-Aware Generation
                  </Typography>
                  <Typography color="text.secondary" sx={{ lineHeight: 1.8 }}>
                    Leverage your organization's past successful proposals, capabilities, and expertise.
                    Our AI crafts tailored responses that match your unique voice and strengths.
                  </Typography>
                </CardContent>
              </Card>
            </Box>

            <Box>
              <Card elevation={2} sx={{ height: '100%', transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-4px)' } }}>
                <CardContent sx={{ p: 4 }}>
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: 2,
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 3,
                    }}
                  >
                    <SpeedIcon sx={{ fontSize: 32, color: 'white' }} />
                  </Box>
                  <Typography variant="h5" fontWeight={600} gutterBottom>
                    Real-Time Progress Tracking
                  </Typography>
                  <Typography color="text.secondary" sx={{ lineHeight: 1.8 }}>
                    Watch your proposal come to life with live updates. Track progress question-by-question
                    and see exactly what sources are being used.
                  </Typography>
                </CardContent>
              </Card>
            </Box>

            <Box>
              <Card elevation={2} sx={{ height: '100%', transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-4px)' } }}>
                <CardContent sx={{ p: 4 }}>
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: 2,
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 3,
                    }}
                  >
                    <TrendingUpIcon sx={{ fontSize: 32, color: 'white' }} />
                  </Box>
                  <Typography variant="h5" fontWeight={600} gutterBottom>
                    Interactive Editing & Export
                  </Typography>
                  <Typography color="text.secondary" sx={{ lineHeight: 1.8 }}>
                    Review, refine, and perfect every answer. Edit generated content in real-time and export
                    professional, ready-to-submit proposals.
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Benefits Section */}
      <Box sx={{ py: 10, bgcolor: 'background.paper' }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 8, alignItems: 'center' }}>
            <Box>
              <Typography variant="h2" fontWeight={700} gutterBottom>
                Stop Losing Opportunities to Time Constraints
              </Typography>
              <Typography variant="h6" color="text.secondary" paragraph sx={{ mb: 4 }}>
                Grant writing shouldn't take weeks. With our AI-powered platform, you can respond to more RFPs,
                maintain quality, and focus on what matters most—your mission.
              </Typography>
              <Stack spacing={3}>
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <CheckCircleIcon color="primary" sx={{ mt: 0.5 }} />
                  <Box>
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      Save 90% of Writing Time
                    </Typography>
                    <Typography color="text.secondary">
                      Transform weeks of work into hours. Generate comprehensive proposals in 2 hours instead of 2 weeks.
                    </Typography>
                  </Box>
                </Stack>
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <CheckCircleIcon color="primary" sx={{ mt: 0.5 }} />
                  <Box>
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      Maintain Consistent Quality
                    </Typography>
                    <Typography color="text.secondary">
                      Every proposal leverages your best work. No more inconsistent quality or missed details.
                    </Typography>
                  </Box>
                </Stack>
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <CheckCircleIcon color="primary" sx={{ mt: 0.5 }} />
                  <Box>
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      Apply to More Grants
                    </Typography>
                    <Typography color="text.secondary">
                      Respond to 10x more opportunities without hiring additional staff or burning out your team.
                    </Typography>
                  </Box>
                </Stack>
              </Stack>
            </Box>
            <Box>
              <Box
                sx={{
                  bgcolor: 'primary.main',
                  borderRadius: 3,
                  p: 4,
                  color: 'white',
                }}
              >
                <Typography variant="h4" fontWeight={700} gutterBottom>
                  The Traditional Way
                </Typography>
                <Stack spacing={2} mb={4}>
                  <Typography>❌ 2-3 weeks per proposal</Typography>
                  <Typography>❌ Manual RFP analysis</Typography>
                  <Typography>❌ Hunting for past proposals</Typography>
                  <Typography>❌ Inconsistent quality</Typography>
                  <Typography>❌ Missing deadlines</Typography>
                </Stack>
                <Typography variant="h4" fontWeight={700} gutterBottom>
                  With Grant Proposal Writer
                </Typography>
                <Stack spacing={2}>
                  <Typography>✅ 2 hours per proposal</Typography>
                  <Typography>✅ Automated RFP extraction</Typography>
                  <Typography>✅ AI-powered context retrieval</Typography>
                  <Typography>✅ Consistent, high-quality output</Typography>
                  <Typography>✅ Meet every deadline</Typography>
                </Stack>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Demo Section */}
      <Box sx={{ py: 10, bgcolor: 'background.default' }}>
        <Container maxWidth="lg">
          <Stack spacing={2} alignItems="center" mb={6}>
            <Typography variant="h2" textAlign="center" fontWeight={700}>
              See It In Action
            </Typography>
            <Typography variant="h6" color="text.secondary" textAlign="center" maxWidth="800px">
              Watch how our AI transforms an RFP into a winning proposal
            </Typography>
          </Stack>
          <Box
            sx={{
              borderRadius: 3,
              overflow: 'hidden',
              boxShadow: 4,
              maxWidth: '900px',
              mx: 'auto',
            }}
          >
            <img
              src="https://placehold.co/900x506/667eea/ffffff?text=Demo+Video+or+Interactive+Preview"
              alt="Product Demo"
              style={{ width: '100%', display: 'block' }}
            />
          </Box>
        </Container>
      </Box>

      {/* Final CTA Section */}
      <Box
        sx={{
          py: 10,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
        }}
      >
        <Container maxWidth="md">
          <Stack spacing={4} alignItems="center" textAlign="center">
            <Typography variant="h2" fontWeight={700}>
              Ready to Win More Grants?
            </Typography>
            <Typography variant="h5" sx={{ opacity: 0.95, maxWidth: '700px' }}>
              Join organizations that are transforming their grant writing process with AI.
              Start your free trial today—no credit card required.
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/signup')}
              sx={{
                bgcolor: 'white',
                color: 'primary.main',
                px: 5,
                py: 2,
                fontSize: '1.2rem',
                mt: 2,
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.9)',
                },
              }}
            >
              Get Started Free
            </Button>
          </Stack>
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ py: 6, bgcolor: 'background.paper', borderTop: '1px solid', borderColor: 'divider' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              Grant Proposal Writer
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
              AI-powered grant proposal generation for nonprofits and organizations seeking funding.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              © 2025 Grant Proposal Writer. All rights reserved.
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}
